import React, { useState, useEffect } from 'react';
import { roomsService, bookingsService, availabilityService, authService } from '../services/api';
import { format } from 'date-fns';

function DebugAvailabilityCalendar() {
  const [debugData, setDebugData] = useState({
    authStatus: null,
    tokenData: null,
    rooms: [],
    bookings: [],
    availability: {},
    errors: [],
    apiCalls: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  const addLog = (message, type = 'info', data = null) => {
    const timestamp = new Date().toISOString();
    setDebugData(prev => ({
      ...prev,
      apiCalls: [...prev.apiCalls, {
        timestamp,
        message,
        type,
        data
      }]
    }));
    console.log(`[DEBUG ${type.toUpperCase()}] ${message}`, data);
  };

  const checkAuthAndLoadData = async () => {
    setLoading(true);
    addLog('Starting debug session...');

    try {
      // 1. Check token existence and validity
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      addLog('Token check', 'info', {
        tokenExists: !!token,
        tokenLength: token?.length,
        userExists: !!user,
        userData: user ? JSON.parse(user) : null
      });

      if (token) {
        try {
          // Decode JWT payload (without verification for debugging)
          const payload = JSON.parse(atob(token.split('.')[1]));
          const now = Date.now() / 1000;
          const isExpired = payload.exp < now;
          
          setDebugData(prev => ({
            ...prev,
            tokenData: {
              payload,
              isExpired,
              expiresAt: new Date(payload.exp * 1000).toISOString(),
              timeRemaining: payload.exp - now
            }
          }));

          addLog('Token decoded', isExpired ? 'error' : 'success', {
            userId: payload.id,
            email: payload.email,
            role: payload.role,
            isExpired,
            expiresAt: new Date(payload.exp * 1000).toISOString()
          });

          if (isExpired) {
            addLog('Token is expired!', 'error');
          }
        } catch (error) {
          addLog('Failed to decode token', 'error', error);
        }
      }

      // 2. Test auth status
      try {
        const currentUser = await authService.getCurrentUser();
        setDebugData(prev => ({
          ...prev,
          authStatus: {
            success: true,
            user: currentUser.data?.user || currentUser.user || currentUser
          }
        }));
        addLog('Auth check successful', 'success', currentUser);
      } catch (error) {
        setDebugData(prev => ({
          ...prev,
          authStatus: {
            success: false,
            error: error.message || error.error || 'Unknown auth error'
          }
        }));
        addLog('Auth check failed', 'error', error);
      }

      // 3. Test rooms API - Fix pagination limit
      try {
        addLog('Fetching rooms...');
        // Fix: Request all rooms by setting a high limit to overcome pagination
        const roomsResponse = await roomsService.getAll({ limit: 100, page: 1 });
        const rooms = roomsResponse.data?.rooms || roomsResponse.rooms || [];
        
        setDebugData(prev => ({
          ...prev,
          rooms
        }));
        addLog(`Rooms fetched successfully (${rooms.length} rooms)`, 'success', {
          totalRooms: rooms.length,
          pagination: {
            total: roomsResponse.data?.total,
            page: roomsResponse.data?.page,
            limit: roomsResponse.data?.limit
          },
          rooms: rooms
        });
      } catch (error) {
        addLog('Rooms fetch failed', 'error', error);
        setDebugData(prev => ({
          ...prev,
          errors: [...prev.errors, `Rooms API Error: ${error.message || error.error}`]
        }));
      }

      // 4. Test bookings API - Fix pagination limit
      try {
        addLog('Fetching bookings...');
        // Fix: Request all bookings by setting a high limit to overcome pagination
        const bookingsResponse = await bookingsService.getAll({ limit: 100, page: 1 });
        const bookings = bookingsResponse.data?.bookings || bookingsResponse.bookings || [];
        
        setDebugData(prev => ({
          ...prev,
          bookings
        }));
        addLog(`Bookings fetched successfully (${bookings.length} bookings)`, 'success', {
          totalBookings: bookings.length,
          pagination: {
            total: bookingsResponse.data?.total,
            page: bookingsResponse.data?.page,
            limit: bookingsResponse.data?.limit
          },
          bookings: bookings
        });
      } catch (error) {
        addLog('Bookings fetch failed', 'error', error);
        setDebugData(prev => ({
          ...prev,
          errors: [...prev.errors, `Bookings API Error: ${error.message || error.error}`]
        }));
      }

      // 5. Test availability API for each room - with updated rooms data
      try {
        addLog('Testing availability API for rooms...');
        // Use the updated rooms data with proper pagination
        const currentRooms = debugData.rooms.length > 0 ? debugData.rooms : 
                           (await roomsService.getAll({ limit: 100, page: 1 })).data?.rooms || [];
        
        if (currentRooms.length > 0) {
          addLog(`Found ${currentRooms.length} rooms total, testing availability for first 10 rooms...`);
          
          for (const room of currentRooms.slice(0, 10)) { // Test first 10 rooms
            try {
              addLog(`Testing availability for room ${room.roomNumber || room.id}...`);
              
              // Use a broader date range that includes July 2025 when bookings were created
              const startDate = new Date('2025-07-01').toISOString(); 
              const endDate = new Date('2025-08-31').toISOString(); 
              
              const availabilityResponse = await availabilityService.getRoomCalendar(room.id, {
                startDate,
                endDate
              });
              
              const roomBookings = availabilityResponse.data?.bookings || [];
              const roomBlocks = availabilityResponse.data?.availabilityBlocks || [];
              
              setDebugData(prev => ({
                ...prev,
                availability: {
                  ...prev.availability,
                  [room.id]: {
                    ...availabilityResponse,
                    roomInfo: {
                      id: room.id,
                      roomNumber: room.roomNumber,
                      isShared: room.roomType?.isShared,
                      bedCount: room.beds?.length || 0
                    }
                  }
                }
              }));
              
              addLog(`Room ${room.roomNumber || room.id} availability:`, 'success', {
                roomId: room.id,
                roomNumber: room.roomNumber,
                bookingsCount: roomBookings.length,
                blocksCount: roomBlocks.length,
                bookings: roomBookings,
                blocks: roomBlocks,
                isShared: room.roomType?.isShared,
                bedCount: room.beds?.length || 0,
                dateRange: { startDate, endDate },
                rawResponse: availabilityResponse
              });

              // Log individual bookings for this room
              if (roomBookings.length > 0) {
                roomBookings.forEach((booking, index) => {
                  addLog(`  Booking ${index + 1} in Room ${room.roomNumber}:`, 'info', {
                    id: booking.id,
                    guestName: booking.userName || booking.user?.name,
                    checkIn: booking.checkIn,
                    checkOut: booking.checkOut,
                    status: booking.status,
                    bedNumber: booking.bedNumber,
                    userId: booking.userId,
                    // Add more booking display analysis
                    displayAnalysis: {
                      hasCheckInOut: !!(booking.checkIn && booking.checkOut),
                      hasGuestName: !!(booking.userName || booking.user?.name),
                      dateFormat: {
                        checkInType: typeof booking.checkIn,
                        checkOutType: typeof booking.checkOut,
                        checkInValid: booking.checkIn ? new Date(booking.checkIn).toString() !== 'Invalid Date' : false,
                        checkOutValid: booking.checkOut ? new Date(booking.checkOut).toString() !== 'Invalid Date' : false
                      }
                    }
                  });
                });
              } else {
                addLog(`  No bookings found for Room ${room.roomNumber} in date range`, 'warning');
              }
              
            } catch (error) {
              addLog(`Availability fetch failed for room ${room.roomNumber || room.id}`, 'error', {
                roomId: room.id,
                roomNumber: room.roomNumber,
                error: error.message,
                status: error.status,
                response: error.response?.data
              });
              setDebugData(prev => ({
                ...prev,
                errors: [...prev.errors, `Availability API Error for Room ${room.roomNumber || room.id}: ${error.message || error.error || 'Unknown error'}`]
              }));
            }
          }
        } else {
          addLog('No rooms found to test availability', 'warning');
        }
      } catch (error) {
        addLog('Failed to test room availability', 'error', error);
      }

    } catch (error) {
      addLog('Critical error in debug session', 'error', error);
    } finally {
      setLoading(false);
      addLog('Debug session completed');
    }
  };

  const clearLogs = () => {
    setDebugData(prev => ({
      ...prev,
      apiCalls: []
    }));
  };

  const testSpecificRoom = async (roomId) => {
    try {
      addLog(`Testing specific room: ${roomId}`);
      const startDate = new Date('2025-07-01').toISOString();
      const endDate = new Date('2025-08-31').toISOString();
      
      const response = await availabilityService.getRoomCalendar(roomId, {
        startDate,
        endDate
      });
      
      addLog(`Room ${roomId} detailed response:`, 'success', response);
      return response;
    } catch (error) {
      addLog(`Room ${roomId} test failed:`, 'error', error);
      return null;
    }
  };

  const testAllBookingsEndpoint = async () => {
    try {
      addLog('Testing all bookings endpoint...');
      // Fix: Request all bookings with proper pagination
      const response = await bookingsService.getAll({ limit: 100, page: 1 });
      addLog('All bookings response:', 'success', {
        count: response.data?.bookings?.length || 0,
        total: response.data?.total,
        pagination: {
          page: response.data?.page,
          limit: response.data?.limit,
          total: response.data?.total
        },
        data: response
      });
      
      // Also test with query parameters
      const todayBookings = await bookingsService.getAll({
        startDate: '2025-07-01',
        endDate: '2025-08-31',
        limit: 100,
        page: 1
      });
      addLog('Bookings with date filter:', 'success', {
        count: todayBookings.data?.bookings?.length || 0,
        total: todayBookings.data?.total,
        data: todayBookings
      });
      
    } catch (error) {
      addLog('All bookings test failed:', 'error', error);
    }
  };

  const testDayaRooms = async () => {
    try {
      addLog('Testing Daya rooms specifically...');
      const rooms = await roomsService.getAll();
      const dayaRooms = (rooms.data?.rooms || []).filter(room => 
        room.roomNumber?.toLowerCase().includes('daya') || 
        room.roomNumber?.includes('Daya')
      );
      
      addLog(`Found ${dayaRooms.length} Daya rooms:`, 'success', dayaRooms);
      
      // Test availability for each Daya room
      for (const room of dayaRooms) {
        await testSpecificRoom(room.id);
      }
      
    } catch (error) {
      addLog('Daya rooms test failed:', 'error', error);
    }
  };

  const crossCheckBookings = async () => {
    try {
      addLog('Cross-checking bookings between admin panel and calendar...');
      
      // Get all bookings from admin endpoint - Fix pagination
      const adminBookings = await bookingsService.getAll({ limit: 100, page: 1 });
      const allBookings = adminBookings.data?.bookings || [];
      
      addLog(`Admin panel shows ${allBookings.length} total bookings (pagination: ${adminBookings.data?.total} total):`, 'info', {
        bookings: allBookings,
        pagination: {
          page: adminBookings.data?.page,
          limit: adminBookings.data?.limit,
          total: adminBookings.data?.total
        }
      });
      
      // Get calendar bookings for each room - Fix pagination
      const rooms = await roomsService.getAll({ limit: 100, page: 1 });
      const allRooms = rooms.data?.rooms || [];
      let totalCalendarBookings = 0;
      const calendarBookingsByRoom = {};
      
      addLog(`Testing calendar endpoints for ${allRooms.length} rooms (pagination: ${rooms.data?.total} total)`, 'info');
      
      for (const room of allRooms) {
        try {
          const calendarData = await availabilityService.getRoomCalendar(room.id, {
            startDate: '2025-07-01T00:00:00.000Z',
            endDate: '2025-08-31T23:59:59.999Z'
          });
          
          const roomBookings = calendarData.data?.bookings || [];
          calendarBookingsByRoom[room.id] = roomBookings;
          totalCalendarBookings += roomBookings.length;
          
          addLog(`Room ${room.roomNumber} calendar data:`, 'info', {
            roomId: room.id,
            roomNumber: room.roomNumber,
            success: calendarData.success,
            bookingsFound: roomBookings.length,
            bookingsData: roomBookings,
            rawResponse: calendarData
          });
          
          if (roomBookings.length > 0) {
            addLog(`Room ${room.roomNumber} has ${roomBookings.length} calendar bookings:`, 'info', roomBookings);
            
            // Log each booking in detail
            roomBookings.forEach((booking, index) => {
              addLog(`  Booking ${index + 1} details:`, 'info', {
                id: booking.id,
                bookingId: booking.bookingId,
                guestName: booking.userName || booking.user?.name,
                checkIn: booking.checkIn,
                checkOut: booking.checkOut,
                startDate: booking.startDate,
                endDate: booking.endDate,
                status: booking.status,
                bedNumber: booking.bedNumber,
                roomId: booking.roomId,
                userId: booking.userId
              });
            });
          } else {
            addLog(`No bookings returned for Room ${room.roomNumber}`, 'warning', {
              roomId: room.id,
              response: calendarData
            });
          }
        } catch (error) {
          addLog(`Failed to get calendar bookings for room ${room.roomNumber}:`, 'error', error);
        }
      }
      
      addLog(`Calendar shows ${totalCalendarBookings} total bookings across all rooms`, 'info');
      
      // Compare the counts
      if (allBookings.length !== totalCalendarBookings) {
        addLog(`DISCREPANCY DETECTED! Admin: ${allBookings.length} vs Calendar: ${totalCalendarBookings}`, 'error', {
          adminBookings: allBookings,
          calendarBookings: calendarBookingsByRoom
        });
      } else {
        addLog('Booking counts match between admin and calendar!', 'success');
      }
      
      // Check individual booking mappings
      for (const adminBooking of allBookings) {
        const roomId = adminBooking.roomId;
        const calendarBookings = calendarBookingsByRoom[roomId] || [];
        const matchingCalendarBooking = calendarBookings.find(cb => cb.id === adminBooking.id);
        
        if (!matchingCalendarBooking) {
          addLog(`Admin booking ${adminBooking.id} not found in calendar for room ${roomId}`, 'error', {
            adminBooking,
            roomCalendarBookings: calendarBookings
          });
        } else {
          addLog(`Booking ${adminBooking.id} found in both admin and calendar`, 'success');
        }
      }
      
    } catch (error) {
      addLog('Cross-check failed:', 'error', error);
    }
  };

  const testCalendarDisplayLogic = async () => {
    try {
      addLog('Testing calendar display logic...');
      
      // Get current state from the component
      addLog('Current debug data state:', 'info', {
        roomsCount: debugData.rooms.length,
        bookingsCount: debugData.bookings.length,
        availabilityKeys: Object.keys(debugData.availability),
        availability: debugData.availability
      });
      
      // Test the calendar logic manually
      const rooms = await roomsService.getAll();
      const allRooms = rooms.data?.rooms || [];
      
      addLog('Testing calendar logic for each room...', 'info');
      
      for (const room of allRooms.slice(0, 3)) {
        addLog(`Testing display logic for Room ${room.roomNumber}...`);
        
        // Get availability data for this room
        const calendarData = await availabilityService.getRoomCalendar(room.id, {
          startDate: '2025-07-01T00:00:00.000Z',
          endDate: '2025-08-31T23:59:59.999Z'
        });
        
        const roomBookings = calendarData.data?.bookings || [];
        
        if (roomBookings.length > 0) {
          addLog(`Room ${room.roomNumber} has bookings, testing calendar display:`, 'info');
          
          // Test how the calendar would process these bookings
          roomBookings.forEach((booking, index) => {
            // Simulate calendar date checking logic
            const checkInDate = new Date(booking.checkIn || booking.startDate);
            const checkOutDate = new Date(booking.checkOut || booking.endDate);
            const today = new Date('2025-07-29'); // Current date
            
            addLog(`  Booking ${index + 1} date analysis:`, 'info', {
              bookingId: booking.id,
              guestName: booking.userName || booking.user?.name,
              checkIn: booking.checkIn,
              checkOut: booking.checkOut,
              checkInDate: checkInDate.toISOString(),
              checkOutDate: checkOutDate.toISOString(),
              todayDate: today.toISOString(),
              isActive: checkInDate <= today && checkOutDate > today,
              isPast: checkOutDate <= today,
              isFuture: checkInDate > today,
              daysDifference: Math.floor((checkInDate - today) / (1000 * 60 * 60 * 24))
            });
            
            // Test if this booking would show up in different calendar views
            const currentMonthStart = new Date('2025-07-01');
            const currentMonthEnd = new Date('2025-07-31');
            const nextMonthStart = new Date('2025-08-01');
            const nextMonthEnd = new Date('2025-08-31');
            
            const showsInJuly = (checkInDate <= currentMonthEnd && checkOutDate >= currentMonthStart);
            const showsInAugust = (checkInDate <= nextMonthEnd && checkOutDate >= nextMonthStart);
            
            addLog(`    Calendar visibility for booking ${booking.id}:`, 'info', {
              showsInJuly,
              showsInAugust,
              wouldShowInCurrentView: showsInJuly || showsInAugust
            });
          });
        } else {
          addLog(`No bookings found for Room ${room.roomNumber} to test display logic`, 'warning');
        }
      }
      
      // Test the component's internal state
      addLog('Testing component state consistency...', 'info');
      
      // Check if the availability data is being stored correctly
      if (Object.keys(debugData.availability).length > 0) {
        Object.entries(debugData.availability).forEach(([roomId, data]) => {
          const roomBookings = data.data?.bookings || [];
          addLog(`Stored availability for room ${roomId}:`, 'info', {
            roomId,
            storedBookingsCount: roomBookings.length,
            roomInfo: data.roomInfo,
            bookings: roomBookings
          });
        });
      } else {
        addLog('No availability data stored in component state', 'error');
      }
      
    } catch (error) {
      addLog('Calendar display logic test failed:', 'error', error);
    }
  };

  const refreshAuth = async () => {
    try {
      addLog('Refreshing authentication...');
      // Clear existing token and try to get current user
      const currentUser = await authService.getCurrentUser();
      addLog('Auth refresh successful', 'success', currentUser);
      
      // Store user data properly
      if (currentUser.data?.user) {
        localStorage.setItem('user', JSON.stringify(currentUser.data.user));
      }
    } catch (error) {
      addLog('Auth refresh failed', 'error', error);
    }
  };

  const fixAuthToken = () => {
    // Set a debug token for testing
    const debugToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6ImNtZGxmaW80eDAwMDF6Mmh4bjUya2hiM24iLCJlbWFpbCI6ImRhdmVAZGF2ZS5jb20iLCJyb2xlIjoiU1VQRVJBRE1JTiIsImlhdCI6MTc1MzYwNjMxOCwiZXhwIjoxNzU5Nzc5MTE4fQ.UC4FRqIn5_0673Ek61H5NTt6fIVWtE5z-qU5jhdrsws';
    localStorage.setItem('token', debugToken);
    localStorage.setItem('user', JSON.stringify({
      id: 'cmdlfio4x0001z2hxn52khb3n',
      email: 'dave@dave.com',
      role: 'SUPERADMIN',
      name: 'Dave Admin'
    }));
    addLog('Debug token set', 'success');
    window.location.reload();
  };

  const analyzeCalendarDisplayIssues = async () => {
    try {
      addLog('Analyzing why bookings are not displaying in calendar UI...');
      
      // Step 1: Simulate the calendar's data loading process
      addLog('Step 1: Simulating calendar data loading process...');
      
      // Get rooms with proper pagination
      const roomsResponse = await roomsService.getAll({ limit: 100, page: 1 });
      const rooms = roomsResponse.data?.rooms || [];
      
      // Get bookings with proper pagination  
      const bookingsResponse = await bookingsService.getAll({ limit: 100, page: 1 });
      const allBookings = bookingsResponse.data?.bookings || [];
      
      addLog(`Found ${rooms.length} rooms and ${allBookings.length} total bookings`, 'info');
      
      // Step 2: Check calendar date range logic (current calendar behavior)
      const currentMonth = new Date(); // July 2025
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1); // Start of month
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0); // End of month
      
      addLog('Step 2: Calendar date range analysis:', 'info', {
        currentMonth: currentMonth.toISOString(),
        calendarStartDate: startDate.toISOString(),
        calendarEndDate: endDate.toISOString(),
        dateRangeInfo: `${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`
      });
      
      // Step 3: Test each room's calendar data like the main component does
      addLog('Step 3: Testing room calendar data loading (main component simulation)...');
      let totalFoundBookings = 0;
      const roomBookingAnalysis = {};
      
      for (const room of rooms.slice(0, 10)) { // Test first 10 rooms
        try {
          // This mimics exactly what AvailabilityCalendar.js does
          const calendarResponse = await availabilityService.getRoomCalendar(room.id, {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          });
          
          const roomBookings = calendarResponse.data?.bookings || [];
          totalFoundBookings += roomBookings.length;
          
          roomBookingAnalysis[room.id] = {
            roomNumber: room.roomNumber,
            rawBookings: roomBookings,
            bookingCount: roomBookings.length
          };
          
          addLog(`Room ${room.roomNumber} calendar analysis:`, roomBookings.length > 0 ? 'success' : 'warning', {
            roomId: room.id,
            roomNumber: room.roomNumber,
            rawBookingsCount: roomBookings.length,
            dateRangeUsed: {
              start: startDate.toISOString(),
              end: endDate.toISOString()
            },
            bookingDetails: roomBookings.map(booking => ({
              id: booking.id,
              dates: `${booking.checkIn} to ${booking.checkOut}`,
              guest: booking.userName || booking.user?.name,
              status: booking.status,
              bedNumber: booking.bedNumber
            }))
          });
          
        } catch (error) {
          addLog(`Failed to analyze room ${room.roomNumber}:`, 'error', error);
        }
      }
      
      // Step 4: Summary and recommendations
      addLog('Calendar Display Analysis Summary:', 'info', {
        totalRoomsAnalyzed: Math.min(rooms.length, 10),
        totalBookingsFound: totalFoundBookings,
        calendarDateRange: `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
        roomAnalysis: roomBookingAnalysis
      });
      
      if (totalFoundBookings === 0) {
        addLog('DIAGNOSIS: No bookings found in current calendar date range', 'error', {
          recommendation: 'Bookings may be outside current month view',
          currentRange: `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
          suggestedAction: 'Check if calendar is showing correct month or navigate to July/August 2025'
        });
      } else {
        addLog(`DIAGNOSIS: Found ${totalFoundBookings} bookings in current calendar view`, 'success', {
          recommendation: 'Bookings should be visible. Check calendar UI rendering.',
          nextSteps: 'Inspect calendar component HTML for booking elements'
        });
      }
      
    } catch (error) {
      addLog('Calendar display analysis failed:', 'error', error);
    }
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#F5F3EB', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h1 style={{ margin: 0, color: '#24332A' }}>Availability Calendar Debug</h1>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={checkAuthAndLoadData}
              disabled={loading}
              style={{
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Running Tests...' : 'Run Debug Tests'}
            </button>
            <button
              onClick={refreshAuth}
              style={{
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Refresh Auth
            </button>
            <button
              onClick={testAllBookingsEndpoint}
              style={{
                backgroundColor: '#A9613C',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Test All Bookings
            </button>
            <button
              onClick={testDayaRooms}
              style={{
                backgroundColor: '#B68235',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Test Daya Rooms
            </button>
            <button
              onClick={crossCheckBookings}
              style={{
                backgroundColor: '#A9613C',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Cross-Check Bookings
            </button>
            <button
              onClick={testCalendarDisplayLogic}
              style={{
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Test Calendar Display
            </button>
            <button
              onClick={analyzeCalendarDisplayIssues}
              style={{
                backgroundColor: '#B68235',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Analyze Display Issues
            </button>
            <button
              onClick={fixAuthToken}
              style={{
                backgroundColor: '#A13F2F',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Fix Token
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Authentication Status */}
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #E3DFD3'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#24332A' }}>Authentication Status</h3>
            
            {debugData.authStatus && (
              <div style={{ marginBottom: '15px' }}>
                <div style={{ 
                  padding: '10px',
                  borderRadius: '6px',
                  backgroundColor: debugData.authStatus.success ? '#E1E9E2' : '#F2D9D3',
                  border: `1px solid ${debugData.authStatus.success ? '#1A3E2A' : '#A13F2F'}`
                }}>
                  <strong style={{ color: debugData.authStatus.success ? '#0F2619' : '#5F241B' }}>
                    {debugData.authStatus.success ? 'Authenticated' : 'Authentication Failed'}
                  </strong>
                  {debugData.authStatus.user && (
                    <div style={{ marginTop: '8px', fontSize: '14px' }}>
                      <div>User: {debugData.authStatus.user.name || debugData.authStatus.user.email}</div>
                      <div>Role: {debugData.authStatus.user.role}</div>
                    </div>
                  )}
                  {debugData.authStatus.error && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: '#5F241B' }}>
                      Error: {debugData.authStatus.error}
                    </div>
                  )}
                </div>
              </div>
            )}

            {debugData.tokenData && (
              <div>
                <h4 style={{ margin: '0 0 10px 0' }}>Token Info:</h4>
                <div style={{ 
                  backgroundColor: '#EEEBE1', 
                  padding: '10px', 
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontFamily: 'monospace'
                }}>
                  <div>User ID: {debugData.tokenData.payload.id}</div>
                  <div>Email: {debugData.tokenData.payload.email}</div>
                  <div>Role: {debugData.tokenData.payload.role}</div>
                  <div style={{ 
                    color: debugData.tokenData.isExpired ? '#A13F2F' : '#1A3E2A'
                  }}>
                    Status: {debugData.tokenData.isExpired ? 'EXPIRED' : 'Valid'}
                  </div>
                  <div>Expires: {format(new Date(debugData.tokenData.payload.exp * 1000), 'PPpp')}</div>
                </div>
              </div>
            )}
          </div>

          {/* Data Summary */}
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #E3DFD3'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#24332A' }}>Data Summary</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div style={{ 
                padding: '15px',
                backgroundColor: '#F5F3EB',
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1A3E2A' }}>
                  {debugData.rooms.length}
                </div>
                <div style={{ fontSize: '14px', color: '#5F6B61' }}>Rooms Loaded</div>
              </div>

              <div style={{ 
                padding: '15px',
                backgroundColor: '#F5F3EB',
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1A3E2A' }}>
                  {debugData.bookings.length}
                </div>
                <div style={{ fontSize: '14px', color: '#5F6B61' }}>Bookings Loaded</div>
              </div>

              <div style={{ 
                padding: '15px',
                backgroundColor: '#F5F3EB',
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#B68235' }}>
                  {Object.keys(debugData.availability).length}
                </div>
                <div style={{ fontSize: '14px', color: '#5F6B61' }}>Rooms Tested</div>
              </div>

              <div style={{ 
                padding: '15px',
                backgroundColor: '#F5F3EB',
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#A13F2F' }}>
                  {debugData.errors.length}
                </div>
                <div style={{ fontSize: '14px', color: '#5F6B61' }}>Errors Found</div>
              </div>
            </div>

            {debugData.errors.length > 0 && (
              <div style={{ marginTop: '15px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#A13F2F' }}>Errors:</h4>
                {debugData.errors.map((error, index) => (
                  <div key={index} style={{ 
                    backgroundColor: '#F2D9D3',
                    padding: '8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    marginBottom: '5px',
                    border: '1px solid #E5B3A9'
                  }}>
                    {error}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Fetched Bookings Display */}
        <div style={{ marginTop: '20px' }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #E3DFD3'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#24332A' }}>Fetched Bookings Analysis</h3>
            
            {debugData.bookings.length > 0 ? (
              <div>
                <div style={{ 
                  marginBottom: '15px',
                  padding: '10px',
                  backgroundColor: '#E1E9E2',
                  borderRadius: '6px',
                  border: '1px solid #163626'
                }}>
                  <strong style={{ color: '#0B1D13' }}>
                    Found {debugData.bookings.length} bookings from admin endpoint
                  </strong>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {debugData.bookings.map((booking, index) => (
                    <div key={booking.id} style={{
                      border: '1px solid #E3DFD3',
                      borderRadius: '8px',
                      padding: '15px',
                      backgroundColor: '#F5F3EB'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px'
                      }}>
                        <h4 style={{ margin: 0, color: '#24332A' }}>
                          Booking #{index + 1}
                        </h4>
                        <div style={{ 
                          backgroundColor: booking.status === 'CONFIRMED' ? '#1A3E2A' : 
                                         booking.status === 'PENDING' ? '#B68235' : '#5F6B61',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {booking.status}
                        </div>
                      </div>
                      
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: '15px',
                        fontSize: '14px'
                      }}>
                        <div>
                          <strong>Guest:</strong> {booking.user?.name || booking.userName || 'Unknown'}<br/>
                          <strong>Email:</strong> {booking.user?.email || booking.userEmail || 'N/A'}<br/>
                          <strong>Booking ID:</strong> {booking.id}<br/>
                          <strong>Room ID:</strong> {booking.roomId}<br/>
                          {booking.bedNumber && <span><strong>Bed:</strong> {booking.bedNumber}<br/></span>}
                        </div>
                        <div>
                          <strong>Check-in:</strong> {booking.checkIn ? new Date(booking.checkIn).toLocaleDateString() : 'N/A'}<br/>
                          <strong>Check-out:</strong> {booking.checkOut ? new Date(booking.checkOut).toLocaleDateString() : 'N/A'}<br/>
                          <strong>Price:</strong> ₹{booking.totalPrice || 0}<br/>
                          <strong>Created:</strong> {booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : 'N/A'}<br/>
                        </div>
                      </div>
                      
                      {/* Show if this booking appears in calendar */}
                      {Object.entries(debugData.availability).length > 0 && (
                        <div style={{ 
                          marginTop: '10px',
                          padding: '8px',
                          backgroundColor: '#F9EEEB',
                          borderRadius: '4px',
                          border: '1px solid #E5B3A9'
                        }}>
                          <strong style={{ color: '#5F241B' }}>Calendar Status:</strong>
                          {(() => {
                            const roomAvailability = debugData.availability[booking.roomId];
                            if (!roomAvailability) {
                              return <span style={{ color: '#5F241B' }}> Room not tested in calendar</span>;
                            }
                            
                            const calendarBookings = roomAvailability.data?.bookings || [];
                            const foundInCalendar = calendarBookings.find(cb => cb.id === booking.id);
                            
                            return foundInCalendar ? 
                              <span style={{ color: '#163626' }}> Found in calendar</span> : 
                              <span style={{ color: '#5F241B' }}> Missing from calendar</span>;
                          })()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center',
                color: '#5F241B',
                fontSize: '16px',
                padding: '40px',
                backgroundColor: '#F2D9D3',
                borderRadius: '6px',
                border: '1px solid #E5B3A9'
              }}>
                No bookings fetched from admin endpoint
              </div>
            )}
          </div>
        </div>

        {/* Availability Analysis */}
        <div style={{ marginTop: '20px' }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #E3DFD3'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#24332A' }}>Availability Analysis</h3>
            
            {Object.keys(debugData.availability).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {Object.entries(debugData.availability).map(([roomId, data]) => {
                  const roomBookings = data.data?.bookings || [];
                  const roomBlocks = data.data?.availabilityBlocks || [];
                  const roomInfo = data.roomInfo || {};
                  
                  return (
                    <div key={roomId} style={{ 
                      border: '1px solid #E3DFD3',
                      borderRadius: '6px',
                      padding: '15px',
                      backgroundColor: '#F5F3EB'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px'
                      }}>
                        <h4 style={{ margin: 0, color: '#24332A' }}>
                          Room {roomInfo.roomNumber || roomId}
                        </h4>
                        <div style={{ display: 'flex', gap: '10px', fontSize: '12px' }}>
                          <span style={{ 
                            backgroundColor: '#1A3E2A',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px'
                          }}>
                            {roomBookings.length} bookings
                          </span>
                          <span style={{ 
                            backgroundColor: '#A9613C',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px'
                          }}>
                            {roomBlocks.length} blocks
                          </span>
                          <span style={{ 
                            backgroundColor: roomInfo.isShared ? '#B68235' : '#1A3E2A',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px'
                          }}>
                            {roomInfo.isShared ? `Shared (${roomInfo.bedCount} beds)` : 'Private'}
                          </span>
                        </div>
                      </div>
                      
                      {roomBookings.length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#1A3E2A' }}>Bookings:</strong>
                          <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {roomBookings.slice(0, 3).map((booking, index) => (
                              <div key={index} style={{ 
                                fontSize: '12px',
                                padding: '4px 8px',
                                backgroundColor: 'white',
                                borderRadius: '3px',
                                border: '1px solid #E3DFD3'
                              }}>
                                {booking.userName || booking.user?.name || 'Guest'} | 
                                {booking.checkIn ? new Date(booking.checkIn).toLocaleDateString() : 'No check-in'} - 
                                {booking.checkOut ? new Date(booking.checkOut).toLocaleDateString() : 'No check-out'} | 
                                Status: {booking.status || 'Unknown'}
                                {booking.bedNumber && ` | Bed ${booking.bedNumber}`}
                              </div>
                            ))}
                            {roomBookings.length > 3 && (
                              <div style={{ fontSize: '11px', color: '#5F6B61', fontStyle: 'italic' }}>
                                +{roomBookings.length - 3} more bookings...
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {roomBookings.length === 0 && (
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#B68235',
                          backgroundColor: '#F3E6CB',
                          padding: '8px',
                          borderRadius: '4px',
                          marginBottom: '10px'
                        }}>
                          No bookings found for this room in the tested date range (July-August 2025)
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center',
                color: '#5F6B61',
                fontSize: '14px',
                padding: '20px'
              }}>
                No availability data tested yet. Click "Run Debug Tests" to analyze room availability.
              </div>
            )}
          </div>
        </div>

        {/* Detailed Data */}
        <div style={{ marginTop: '20px' }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #E3DFD3'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <h3 style={{ margin: 0, color: '#24332A' }}>Rooms & Availability Data</h3>
            </div>

            {debugData.rooms.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                {debugData.rooms.slice(0, 6).map(room => (
                  <div key={room.id} style={{ 
                    border: '1px solid #E3DFD3',
                    borderRadius: '6px',
                    padding: '15px',
                    backgroundColor: '#F5F3EB'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>
                      Room {room.roomNumber}
                    </h4>
                    <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                      <div><strong>Type:</strong> {room.roomType?.name || 'Unknown'}</div>
                      <div><strong>Shared:</strong> {room.roomType?.isShared ? 'Yes' : 'No'}</div>
                      {room.beds && <div><strong>Beds:</strong> {room.beds.length}</div>}
                      
                      {debugData.availability[room.id] && (
                        <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#E1E9E2', borderRadius: '4px' }}>
                          <strong>Availability Status:</strong>
                          <div style={{ fontSize: '12px', marginTop: '5px' }}>
                            {debugData.availability[room.id].success ? 'API Success' : 'API Failed'}
                          </div>
                          {debugData.availability[room.id].data && (
                            <div style={{ fontSize: '12px' }}>
                              Bookings: {debugData.availability[room.id].data.bookings?.length || 0} |
                              Blocks: {debugData.availability[room.id].data.availabilityBlocks?.length || 0}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                color: '#5F6B61',
                backgroundColor: '#F5F3EB',
                borderRadius: '6px'
              }}>
                No rooms data available
              </div>
            )}
          </div>
        </div>

        {/* API Call Logs */}
        <div style={{ marginTop: '20px' }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #E3DFD3'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <h3 style={{ margin: 0, color: '#24332A' }}>API Call Logs</h3>
              <button
                onClick={clearLogs}
                style={{
                  backgroundColor: '#5F6B61',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Clear Logs
              </button>
            </div>

            <div style={{ 
              maxHeight: '400px',
              overflowY: 'auto',
              backgroundColor: '#24332A',
              color: '#F5F3EB',
              padding: '15px',
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '12px'
            }}>
              {debugData.apiCalls.map((call, index) => (
                <div key={index} style={{ 
                  marginBottom: '8px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid #36433B'
                }}>
                  <div style={{ 
                    color: call.type === 'error' ? '#E5B3A9' : 
                          call.type === 'success' ? '#C4D4C8' : '#E8CF9C'
                  }}>
                    [{format(new Date(call.timestamp), 'HH:mm:ss')}] {call.message}
                  </div>
                  {call.data && (
                    <div style={{ 
                      marginTop: '4px',
                      paddingLeft: '20px',
                      color: '#D2CDBF',
                      fontSize: '11px'
                    }}>
                      {JSON.stringify(call.data, null, 2).substring(0, 200)}
                      {JSON.stringify(call.data, null, 2).length > 200 && '...'}
                    </div>
                  )}
                </div>
              ))}
              
              {debugData.apiCalls.length === 0 && (
                <div style={{ color: '#A5A89B', fontStyle: 'italic' }}>
                  No logs yet. Click "Run Debug Tests" to start.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DebugAvailabilityCalendar;
